import React, { useState, useEffect, useRef, useCallback } from "react";
import loadSoundboardData from "./soundboardData";
import { generateClient } from "aws-amplify/api";
import { signOut } from "aws-amplify/auth";
import { uploadData, getUrl, list, remove } from "aws-amplify/storage";
import {
  Button,
  Snackbar,
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Slider,
  Paper,
  IconButton,
  Card,
  CardContent,
  Collapse,
  Grid,
  Chip,
} from "@mui/material";
import CustomAuthenticator from "./CustomAuthenticator";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
});

const client = generateClient();

const SOUNDBOARD_COLORS = [
  '#c62828', '#e65100', '#2e7d32', '#00695c', '#1565c0',
  '#4527a0', '#ad1457', '#00838f', '#558b2f', '#4e342e',
];

// Storage is configured in aws-exports.js or in App2.js

function Admin2() {
  // State management
  const [seek, setSeek] = useState(0);
  const [movies, setMovies] = useState([]);
  const [whatsPlaying, setWhatsPlaying] = useState("");
  const [time, setTime] = useState(0);
  const [isOn, setIsOn] = useState(false);
  const [start, setStart] = useState(0);
  const [dragAlong, setDragAlong] = useState(false);
  const [dragAlongTime, setDragAlongTime] = useState(60);
  const [statusBar, setStatusBar] = useState(false);
  const [statusBarText, setStatusBarText] = useState("");
  const [rmBrownExpanded, setRmBrownExpanded] = useState(false);
  const [rmBrownItems, setRmBrownItems] = useState([]);
  const [buttonColors, setButtonColors] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('rmbrownColors') || '{}');
    } catch { return {}; }
  });

  // Refs for timers
  const timer = useRef(null);
  const catchupTimer = useRef(null);
  const mutations = useRef(null);

  // Load mutations dynamically to avoid build issues
  useEffect(() => {
    async function loadMutations() {
      try {
        mutations.current = await import("./graphql/mutations");
      } catch (error) {
        // Failed to load mutations
      }
    }
    loadMutations();
  }, []);

  // Display status messages
  const showStatus = (message) => {
    setStatusBar(true);
    setStatusBarText(message);
  };

  // Fetch movies from S3
  const getMovies = useCallback(async () => {
    try {
      const result = await list({
        path: "",
        options: {
          bucket: {
            bucketName: "watchwithtoonss3",
            region: "af-south-1",
          },
        },
      });
      setMovies(result.items.filter((item) => item.path !== "public/"));
    } catch (error) {
      showStatus("Failed to fetch movies");
    }
  }, []);

  // Main timer effect for time updates and drag along functionality
  useEffect(() => {
    if (!isOn) return;

    // Clear existing timer
    clearInterval(timer.current);

    // Set new timer for updating time and handling drag along
    timer.current = setInterval(() => {
      console.log({
        time,
        dragAlongTime,
        dragAlong,
        isOn,
        start,
        whatsPlaying,
        seek,
      });

      // Update the time
      setTime(Date.now() - start);

      // Handle drag along - send seek command every dragAlongTime seconds (default 60)
      if (dragAlong && mutations.current) {
        // Use exact seconds elapsed, not formatted string
        const currentSeconds = Math.floor((Date.now() - start) / 1000);

        // Only send command when we're at a multiple of dragAlongTime (60 seconds)
        if (
            currentSeconds > 0 &&
            currentSeconds % (dragAlongTime * 1000) === 0
        ) {
          console.log({ currentSeconds, dragAlongTime, dragAlong, isOn });
          client
              .graphql({
                query: mutations.current.createWatchwithToonsMessages,
                variables: {
                  input: {
                    // Send the current position
                    name: currentSeconds.toString(),
                    command: "seek",
                  },
                },
              })
              .catch(() => {});
        }
      }
    }, 1000);

    return () => clearInterval(timer.current);
  }, [isOn, dragAlong, dragAlongTime, start]);

  // Separate effect for catchup commands
  useEffect(() => {
    if (!isOn) return;

    // Clear existing timer
    clearInterval(catchupTimer.current);

    // Set new timer for catchup commands
    catchupTimer.current = setInterval(() => {
      // Make sure we're sending the path without the public/ prefix
      if (dragAlong) {
        const cleanPath = whatsPlaying.replace("public/", "");
        sendCommand(cleanPath + "|" + seek, "catchup");
      }
    }, 10000);

    return () => clearInterval(catchupTimer.current);
  }, [isOn, whatsPlaying, seek]);

  const startTimer = useCallback(() => {
    setIsOn(true);
    setStart(Date.now() - time);
  }, [time]);

  const stopTimer = useCallback(() => {
    setIsOn(false);
  }, []);

  const resetTimer = useCallback(() => {
    setTime(0);
    setIsOn(false);
  }, []);

  // Send commands to the GraphQL API
  const sendCommand = useCallback(
      async (input, command) => {
        if (!mutations.current) {
          // Mutations not loaded yet
          return;
        }

        // Handle command-specific actions
        switch (command) {
          case "play":
            setWhatsPlaying(input);
            startTimer();
            break;
          case "start":
            startTimer();
            break;
          case "load":
            setWhatsPlaying(input);
            stopTimer();
            resetTimer();
            break;
          case "stop":
            setWhatsPlaying("");
            stopTimer();
            resetTimer();
            break;
          case "pause":
            stopTimer();
            break;
          case "unpause":
            startTimer();
            break;
          case "soundboard":
            // Just send the command, no state changes needed
            break;
          default:
            break;
        }

        // Send the command via GraphQL
        try {
          await client.graphql({
            query: mutations.current.createWatchwithToonsMessages,
            variables: {
              input: {
                name: input,
                command: command,
              },
            },
          });
        } catch (error) {
          showStatus("Failed to send command: " + command);
        }
      },
      [startTimer, stopTimer, resetTimer]
  );

  // Delete a file from S3
  const deleteFile = useCallback(
      async (path) => {
        try {
          await remove({
            path,
            options: {
              bucket: {
                bucketName: "watchwithtoonss3",
                region: "af-south-1",
              },
            },
          });
          showStatus(`Deleted ${path}`);
          getMovies();
        } catch (error) {
          showStatus("Failed to delete file");
        }
      },
      [getMovies]
  );

  // Upload a file to S3
  const uploadFile = useCallback(
      async (file) => {
        if (!file) return;

        try {
          await uploadData({
            key: file.name,
            data: file,
            options: {
              bucket: {
                bucketName: "watchwithtoonss3",
                region: "af-south-1",
              },
              onProgress: ({ transferredBytes, totalBytes }) => {
                const percentComplete = (
                    (transferredBytes * 100) /
                    totalBytes
                ).toFixed(2);
                showStatus(`Uploading ${file.name}: ${percentComplete}%`);
              },
            },
          }).result;

          showStatus(`Upload complete: ${file.name}`);
          getMovies();
        } catch (error) {
          showStatus(`Upload failed: ${file.name}`);
        }
      },
      [getMovies]
  );

  // Upload multiple files sequentially
  const uploadFiles = useCallback(
      async (files) => {
        for (const file of files) {
          await uploadFile(file);
        }
      },
      [uploadFile]
  );

  // Fetch RM Brown soundboard from local JSON file
  useEffect(() => {
    const fetchRmBrownSoundboard = async () => {
      try {
        const data = await loadSoundboardData();

        // Apply saved positions immediately after data loads
        const savedPositions = localStorage.getItem('rmbrownPositions');
        if (savedPositions) {
          try {
            const positions = JSON.parse(savedPositions);
            // positions is an ordered array of {name} or we stored the reordered items
            // Reorder data according to saved order
            const reordered = positions
              .map(pos => data.find(item => item.name === pos.name))
              .filter(Boolean);
            // Add any new items not in saved positions at the end
            const savedNames = new Set(positions.map(p => p.name));
            const newItems = data.filter(item => !savedNames.has(item.name));
            setRmBrownItems([...reordered, ...newItems]);
          } catch (e) {
            setRmBrownItems(data);
          }
        } else {
          setRmBrownItems(data);
        }

        console.log('RM Brown soundboard loaded:', data.length, 'sounds');
      } catch (error) {
        console.log('Failed to load RM Brown soundboard:', error);
        showStatus('Failed to load RM Brown soundboard');
      }
    };

    fetchRmBrownSoundboard();
  }, []);

  // Load movies on mount
  useEffect(() => {
    getMovies();

    return () => {
      clearInterval(timer.current);
      clearInterval(catchupTimer.current);
    };
  }, [getMovies]);

  const handleRightClick = (e, itemName) => {
    e.preventDefault();
    setButtonColors(prev => {
      const current = prev[itemName];
      let next;
      if (current === undefined || current === null) {
        next = { ...prev, [itemName]: 0 };
      } else if (current >= SOUNDBOARD_COLORS.length - 1) {
        const updated = { ...prev };
        delete updated[itemName];
        next = updated;
      } else {
        next = { ...prev, [itemName]: current + 1 };
      }
      localStorage.setItem('rmbrownColors', JSON.stringify(next));
      return next;
    });
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();

    const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));

    if (draggedIndex === targetIndex) {
      return;
    }

    // Reorder the items
    const newItems = [...rmBrownItems];
    const draggedItem = newItems[draggedIndex];

    // Remove dragged item
    newItems.splice(draggedIndex, 1);
    // Insert at new position
    newItems.splice(targetIndex, 0, draggedItem);

    setRmBrownItems(newItems);

    // Save to localStorage
    const positions = newItems.map((item, index) => ({
      name: item.name,
      position: index
    }));
    localStorage.setItem('rmbrownPositions', JSON.stringify(positions));
  };

  // UI Rendering
  return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        {/* Status notifications */}
        <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            open={statusBar}
            autoHideDuration={30000}
            onClose={() => setStatusBar(false)}
            message={statusBarText}
        />

        {/* App Bar with Sign Out */}
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Movie Player Admin
            </Typography>
            <Button color="inherit" onClick={() => signOut()}>
              Sign Out
            </Button>
          </Toolbar>
        </AppBar>

        <Container maxWidth="100%" sx={{ mt: 4, mb: 4 }}>
          {/* RM Brown Soundboard Section */}
          <Paper elevation={3} sx={{ mb: 4 }}>
            <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setRmBrownExpanded(!rmBrownExpanded)}
            >
              <Typography variant="h5" sx={{ flexGrow: 1 }}>
                Soundboard ({rmBrownItems.length} sounds)
              </Typography>
              <IconButton
                  sx={{
                    transform: rmBrownExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s",
                  }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
            <Collapse in={rmBrownExpanded}>
              <Box sx={{ p: 2, pt: 0 }}>
                <Grid container spacing={1}>
                  {rmBrownItems
                      .map((item, idx) => {
                        const displayName = item.label || item.name.replace(/-/g, ' ');
                        return (
                            <Grid
                                item
                                xs={4}
                                sm={3}
                                md={2}
                                lg={1}
                                key={item.name}
                                draggable
                                onDragStart={(e) => handleDragStart(e, idx)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, idx)}
                            >
                              <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    p: 1,
                                  }}
                              >

                                <Button
                                    variant={buttonColors[item.name] !== undefined ? "contained" : "outlined"}
                                    size="small"
                                    fullWidth
                                    sx={{
                                      fontSize: '0.7rem',
                                      textTransform: 'none',
                                      whiteSpace: 'normal',
                                      wordWrap: 'break-word',
                                      minHeight: '40px',
                                      padding: '4px 8px',
                                      ...(buttonColors[item.name] !== undefined && {
                                        backgroundColor: SOUNDBOARD_COLORS[buttonColors[item.name]],
                                        '&:hover': { backgroundColor: SOUNDBOARD_COLORS[buttonColors[item.name]], filter: 'brightness(1.2)' },
                                      }),
                                    }}
                                    onClick={() => sendCommand(`https://rm-brown-soundboard.com/sounds/drops/${item.name}.mp3`, "soundboard")}
                                    onContextMenu={(e) => handleRightClick(e, item.name)}
                                >
                                  {displayName}
                                </Button>
                              </Box>
                            </Grid>
                        );
                      })}
                </Grid>
              </Box>
            </Collapse>
          </Paper>
          {/* File Upload Section */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Upload Movie
            </Typography>
            <Box sx={{ mt: 2 }}>
              <input
                  type="file"
                  name="upload-movie"
                  multiple
                  onChange={(event) => {
                    if (event.target.files && event.target.files.length > 0) {
                      uploadFiles(Array.from(event.target.files));
                    }
                  }}
                  style={{ marginBottom: "10px", display: "block" }}
              />
            </Box>
          </Paper>

          {/* Movie List Section */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="h5" sx={{ flexGrow: 1 }}>
                Available Movies
              </Typography>
              <IconButton
                  color="primary"
                  onClick={getMovies}
                  aria-label="refresh movies list"
              >
                <RefreshIcon />
              </IconButton>
            </Box>

            {movies.length === 0 && <Typography>No movies available</Typography>}

            <Grid container spacing={2}>
              {movies.map((movie, idx) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="body1" sx={{ mb: 1, wordBreak: "break-word" }}>
                          {movie.path.replace("public/", "")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                              color={
                                whatsPlaying === movie.path.replace("public/", "")
                                    ? "error"
                                    : "primary"
                              }
                              onClick={() =>
                                  sendCommand(movie.path.replace("public/", ""), "play")
                              }
                              aria-label="play movie"
                          >
                            <PlayArrowIcon />
                          </IconButton>
                          <IconButton
                              color="error"
                              onClick={() => deleteFile(movie.path)}
                              aria-label="delete movie"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Playback Controls Section */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Playback Controls
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
              <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    sendCommand("", "stop");
                    setWhatsPlaying("");
                  }}
                  size="medium"
              >
                Stop Playing
              </Button>
              <IconButton
                  color={isOn ? "error" : "primary"}
                  onClick={() => {
                    if (isOn) {
                      sendCommand("", "pause");
                      stopTimer();
                    } else {
                      sendCommand("", "unpause");
                      startTimer();
                    }
                  }}
                  size="large"
              >
                <PauseIcon />
              </IconButton>
              <Button
                  variant="contained"
                  color={dragAlong ? "error" : "primary"}
                  onClick={() => setDragAlong(!dragAlong)}
              >
                Drag Along: {dragAlong ? "ON" : "OFF"}
              </Button>

              {/* Seek Controls */}
              <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    mt: 2,
                  }}
              >
                <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      mb: 2,
                    }}
                >
                  <Typography sx={{ mr: 2, minWidth: "50px" }}>
                    {Math.floor(seek / 60)}m
                  </Typography>
                  <Slider
                      value={seek / 60}
                      min={0}
                      max={300}
                      step={1}
                      onChange={(e, newValue) => setSeek(newValue * 60)}
                      marks={[
                        { value: 0, label: "0m" },
                        { value: 60, label: "60m" },
                        { value: 120, label: "120m" },
                        { value: 180, label: "180m" },
                        { value: 240, label: "240m" },
                        { value: 300, label: "300m" },
                      ]}
                      aria-labelledby="seek-slider"
                      sx={{ flexGrow: 1 }}
                  />
                </Box>
                {/* Helper function to format time */}
                {(() => {
                  const hours = Math.floor(seek / 3600);
                  const minutes = Math.floor((seek % 3600) / 60);
                  const timeDisplay =
                      hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

                  return (
                      <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            sendCommand(seek.toString(), "seek");
                            setTime(seek * 1000);
                            setStart(Date.now() - seek * 1000);
                          }}
                      >
                        Seek to {timeDisplay}
                      </Button>
                  );
                })()}
              </Box>
            </Box>
          </Paper>
        </Container>
      </ThemeProvider>
  );
}

export default function AuthenticatedAdmin2() {
  return (
      <CustomAuthenticator>
        <Admin2 />
      </CustomAuthenticator>
  );
}